import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Divider,
  CircularProgress,
  Chip,
  IconButton,
  Link,
  Slider,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import axiosInstance from "../../../../api/axiosConfig";
import { faNairaSign } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";

function TokenDetailsModal({ open, onClose, tokenId }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  // Buy Flow States
  const [showBuyFlow, setShowBuyFlow] = useState(false);
  const [amount, setAmount] = useState(100000);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [purchaseMethod, setPurchaseMethod] = useState("wallet");
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [transferDetails, setTransferDetails] = useState(null);

  useEffect(() => {
    if (open && tokenId) {
      fetchTokenDetails();
    } else {
      setDetails(null);
    }
  }, [open, tokenId]);

  useEffect(() => {
    if (!open) {
      setShowBuyFlow(false);
      setAmount(100000);
      setCustomerName("");
      setCustomerEmail("");
      setPurchaseMethod("wallet");
      setPaymentMethod("bank_transfer");
      setTransferDetails(null);
      setSubmitting(false);
    } else if (details) {
      setAmount(details?.min_trade?.buy || 100000);
    }
  }, [open, details]);

  const fetchTokenDetails = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        "/marketplace/getequity/get-single-token",
        {
          params: { token_id: tokenId },
        },
      );
      setDetails(response.data?.data);
    } catch (error) {
      console.error("Error fetching token details:", error);
    } finally {
      setLoading(false);
    }
  };

  // Calculated values for the slider & units
  const unitPrice = details?.price?.buy || 10000;
  const minAmount = details?.min_trade?.buy || 100000;
  const maxAmount = details?.max_trade?.buy || 20000000;
  const units = amount / unitPrice;

  const handleBuy = async () => {
    if (!customerName.trim() || !customerEmail.trim()) {
      toast.error("Please complete your name and email.");
      return;
    }

    if (!units || units <= 0) {
      toast.error("Please select a valid investment amount.");
      return;
    }

    if (
      purchaseMethod === "direct" &&
      !["card", "bank_transfer"].includes(paymentMethod)
    ) {
      toast.error("Please select a valid payment method.");
      return;
    }

    try {
      setSubmitting(true);

      /*
       * OPTION 1:
       * Purchase using existing wallet balance.
       */
      if (purchaseMethod === "wallet") {
        const response = await axiosInstance.post(
          "/marketplace/getequity/buy-token/",
          {
            token_id: tokenId,
            name: customerName.trim(),
            email: customerEmail.trim(),
            units,
          },
        );

        const neededAmount = response.data?.data?.neededAmount;

        toast.success(
          neededAmount
            ? `Token purchased successfully! Spent NGN ${Number(
                neededAmount,
              ).toLocaleString()}`
            : "Token purchased successfully!",
        );

        onClose();
        return;
      }

      /*
       * OPTION 2:
       * Fund and invest directly.
       */
      const response = await axiosInstance.post(
        "/marketplace/getequity/customer-fund-invest/",
        {
          token_id: tokenId,
          name: customerName.trim(),
          email: customerEmail.trim(),
          units,
          method: paymentMethod,
        },
      );

      const data = response.data?.data;

      if (paymentMethod === "card") {
        if (!data?.paymentLink) {
          toast.error("Payment link was not returned.");
          return;
        }

        window.location.href = data.paymentLink;
        return;
      }

      if (paymentMethod === "bank_transfer") {
        if (!data?.virtualAccount?.accountNumber) {
          toast.error("Virtual account details were not returned.");
          return;
        }

        setTransferDetails(data);
        return;
      }

      toast.error("Unexpected response from the server.");
    } catch (error) {
      console.error("Error purchasing token:", error);

      toast.error(
        error.response?.data?.message ||
          "An error occurred while processing the investment.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const memoHtml = details?.asset?.memo?.memo;
  const documents = details?.asset?.documents || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#ffffff",
          color: "#000000",
          borderRadius: 0,
          border: "1px solid #000000",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justify: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
          {loading ? "Loading Asset..." : details?.name || "Asset Details"}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "#000000" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: "#000000" }} />
          </Box>
        ) : !details ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 4 }}
          >
            Failed to load asset details.
          </Typography>
        ) : (
          <Box>
            {/* Asset Header Info */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
                mb: 3,
                mt: 2,
              }}
            >
              {details.image && (
                <Box
                  component="img"
                  src={details.image}
                  alt={details.name}
                  sx={{
                    width: 72,
                    height: 72,
                    objectFit: "contain",
                    border: "1px solid #e0e0e0",
                    p: 1,
                    bgcolor: "#fcfcfc",
                  }}
                />
              )}
              <Box sx={{ flexGrow: 1 }}>
                <Box
                  sx={{ display: "flex", gap: 1, mb: 0.5, flexWrap: "wrap" }}
                >
                  <Chip
                    label={details.investment_category}
                    size="small"
                    sx={{
                      borderRadius: 0,
                      bgcolor: "#000000",
                      color: "#ffffff",
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    label={`Risk: ${details.risk || "N/A"}`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderRadius: 0,
                      borderColor: "#000000",
                      color: "#000000",
                    }}
                  />
                  {details.rating && (
                    <Chip
                      label={`Rating: ${details.rating}`}
                      size="small"
                      variant="outlined"
                      sx={{ borderRadius: 0, borderColor: "#e0e0e0" }}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Symbol: {details.symbol} | Custodian:{" "}
                  {details.custodian || "-"}
                </Typography>
              </Box>
            </Box>

            {/* Financial Highlights Table Grid */}
            <Box
              sx={{
                border: "1px solid #000000",
                p: 2,
                bgcolor: "#fcfcfc",
                mb: 3,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Implied Yield
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {details.interest}% p.a
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Tenor
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {details.tenor} Days
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Unit Price
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    <FontAwesomeIcon icon={faNairaSign} />
                    {details.price?.buy?.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Target Raise
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    <FontAwesomeIcon icon={faNairaSign} />
                    {details.raise_amount?.toLocaleString()}
                  </Typography>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Min Subscription
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    <FontAwesomeIcon icon={faNairaSign} />
                    {details.min_trade?.buy?.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Max Subscription
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    <FontAwesomeIcon icon={faNairaSign} />
                    {details.max_trade?.buy?.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Buy Fee
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {details.buy_fee}%
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Payout
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {details.payout_frequency}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* About / Description */}
            {details.asset?.about && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 1, textTransform: "uppercase" }}
                >
                  About Issuer
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.6 }}
                >
                  {details.asset.about}
                </Typography>
              </Box>
            )}

            {/* Investment Memo HTML Content */}
            {memoHtml && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 1, textTransform: "uppercase" }}
                >
                  Investment Offering Memo
                </Typography>
                <Box
                  sx={{
                    fontSize: "0.875rem",
                    color: "#333333",
                    lineHeight: 1.6,
                    "& p": { mb: 1.5 },
                    "& ul, & ol": { pl: 3, mb: 1.5 },
                    "& h3": { fontSize: "1rem", fontWeight: 700, mt: 2, mb: 1 },
                  }}
                  dangerouslySetInnerHTML={{ __html: memoHtml }}
                />
              </Box>
            )}

            {/* Documents List */}
            {documents.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 1, textTransform: "uppercase" }}
                >
                  Documents
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {documents.map((doc) => (
                    <Box
                      key={doc._id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justify: "space-between",
                        p: 1.5,
                        border: "1px solid #e0e0e0",
                        bgcolor: "#fafafa",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <DescriptionIcon
                          fontSize="small"
                          sx={{ color: "#000000" }}
                        />
                        <Typography variant="body2" fontWeight={600}>
                          {doc.name} ({doc.type})
                        </Typography>
                      </Box>
                      <Link
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="none"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          color: "#000000",
                          fontWeight: 600,
                          fontSize: "0.8rem",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        View <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </Link>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Buy Flow Section */}
        {showBuyFlow && (
          <>
            {transferDetails ? (
              <Box sx={{ mt: 3, pt: 3, borderTop: "2px solid #000000" }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    textTransform: "uppercase",
                  }}
                >
                  Complete Bank Transfer
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Transfer the exact amount below. Your investment will be
                  credited after the transfer is confirmed.
                </Typography>

                <Box
                  sx={{
                    border: "1px solid #000000",
                    p: 2,
                    bgcolor: "#fcfcfc",
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      BANK NAME
                    </Typography>

                    <Typography variant="body2" fontWeight={700}>
                      {transferDetails.virtualAccount?.bankName}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      ACCOUNT NUMBER
                    </Typography>

                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ letterSpacing: 1 }}
                    >
                      {transferDetails.virtualAccount?.accountNumber}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      AMOUNT TO PAY
                    </Typography>

                    <Typography variant="body2" fontWeight={700}>
                      {transferDetails.currency}{" "}
                      {Number(
                        transferDetails.amountToPay ||
                          transferDetails.totalCharge ||
                          transferDetails.virtualAccount?.amount ||
                          0,
                      ).toLocaleString()}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      ACCOUNT NOTE
                    </Typography>

                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{ textAlign: "right", maxWidth: "65%" }}
                    >
                      {transferDetails.virtualAccount?.note || "-"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      EXPIRES AT
                    </Typography>

                    <Typography variant="caption" fontWeight={700}>
                      {transferDetails.virtualAccount?.expiresAt}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="caption">Investment Amount:</Typography>

                  <Typography variant="caption">
                    {transferDetails.currency}{" "}
                    {Number(
                      transferDetails.investmentAmount || 0,
                    ).toLocaleString()}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="caption">Tokens to Credit:</Typography>

                  <Typography variant="caption">
                    {Number(
                      transferDetails.tokensToCredit || 0,
                    ).toLocaleString()}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="caption">GetEquity Fee:</Typography>

                  <Typography variant="caption">
                    {transferDetails.currency}{" "}
                    {Number(
                      transferDetails.fees?.getequity || 0,
                    ).toLocaleString()}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="caption">Payment Fee:</Typography>

                  <Typography variant="caption">
                    {transferDetails.currency}{" "}
                    {Number(
                      transferDetails.fees?.flutterwave || 0,
                    ).toLocaleString()}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    pt: 1,
                    borderTop: "1px solid #e0e0e0",
                  }}
                >
                  <Typography variant="body2" fontWeight={700}>
                    Total Charge:
                  </Typography>

                  <Typography variant="body2" fontWeight={700}>
                    {transferDetails.currency}{" "}
                    {Number(transferDetails.totalCharge || 0).toLocaleString()}
                  </Typography>
                </Box>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 2 }}
                >
                  Reference: {transferDetails.reference}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mt: 3, pt: 3, borderTop: "2px solid #000000" }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 2, textTransform: "uppercase" }}
                >
                  Purchase Investment
                </Typography>

                {/* Slider */}
                <Typography variant="caption" color="text.secondary">
                  Select Investment Amount
                </Typography>
                <Slider
                  value={amount}
                  min={minAmount}
                  max={maxAmount}
                  step={unitPrice}
                  onChange={(e, newValue) => setAmount(newValue)}
                  sx={{
                    color: "#000000",
                    my: 1,
                    "& .MuiSlider-thumb": { borderRadius: 0 },
                  }}
                />

                {/* Amount & Units Readout */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                    p: 1.5,
                    bgcolor: "#f5f5f5",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Typography variant="body2">
                    Amount:{" "}
                    <strong>
                      {details?.currency || "NGN"} {amount.toLocaleString()}
                    </strong>
                  </Typography>
                  <Typography variant="body2">
                    Units: <strong>{units.toLocaleString()}</strong>
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "#333333",
                      textTransform: "uppercase",
                    }}
                  >
                    How do you want to pay?
                  </Typography>

                  <RadioGroup
                    value={purchaseMethod}
                    onChange={(e) => {
                      setPurchaseMethod(e.target.value);
                      setTransferDetails(null);
                    }}
                    sx={{ mt: 1 }}
                  >
                    <FormControlLabel
                      value="wallet"
                      control={
                        <Radio
                          sx={{
                            color: "#000000",
                            "&.Mui-checked": {
                              color: "#000000",
                            },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Invest with Wallet
                          </Typography>

                          <Typography variant="caption" color="text.secondary">
                            Use your existing GetEquity wallet balance.
                          </Typography>
                        </Box>
                      }
                    />

                    <FormControlLabel
                      value="direct"
                      control={
                        <Radio
                          sx={{
                            color: "#000000",
                            "&.Mui-checked": {
                              color: "#000000",
                            },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Pay & Invest
                          </Typography>

                          <Typography variant="caption" color="text.secondary">
                            Fund this investment directly using card or bank
                            transfer.
                          </Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </Box>

                {/* Form Inputs */}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Customer Name"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 0,
                          "&.Mui-focused fieldset": { borderColor: "#000000" },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="email"
                      label="Customer Email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 0,
                          "&.Mui-focused fieldset": { borderColor: "#000000" },
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                {purchaseMethod === "direct" && (
                  <Box sx={{ mt: 3 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#333333",
                        textTransform: "uppercase",
                      }}
                    >
                      Payment Method
                    </Typography>

                    <RadioGroup
                      value={paymentMethod}
                      onChange={(e) => {
                        setPaymentMethod(e.target.value);
                        setTransferDetails(null);
                      }}
                      row
                      sx={{ mt: 1 }}
                    >
                      <FormControlLabel
                        value="card"
                        control={
                          <Radio
                            sx={{
                              color: "#000000",
                              "&.Mui-checked": {
                                color: "#000000",
                              },
                            }}
                          />
                        }
                        label="Pay with Card"
                      />

                      <FormControlLabel
                        value="bank_transfer"
                        control={
                          <Radio
                            sx={{
                              color: "#000000",
                              "&.Mui-checked": {
                                color: "#000000",
                              },
                            }}
                          />
                        }
                        label="Pay with Transfer"
                      />
                    </RadioGroup>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <Divider />

      {/* Actions */}
      <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{ color: "#000000", borderRadius: 0, textTransform: "none" }}
        >
          Close
        </Button>

        {details &&
          !details.cancelled &&
          !details.closed &&
          !details.completed_raise && (
            <>
              {!showBuyFlow ? (
                <Button
                  onClick={() => setShowBuyFlow(true)}
                  disabled={loading || !details}
                  variant="contained"
                  sx={{
                    bgcolor: "#000000",
                    color: "#ffffff",
                    borderRadius: 0,
                    px: 4,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#333333" },
                  }}
                >
                  Buy Asset
                </Button>
              ) : (
                !transferDetails && (
                  <Button
                    onClick={handleBuy}
                    disabled={
                      submitting ||
                      !customerName.trim() ||
                      !customerEmail.trim() ||
                      units <= 0
                    }
                    variant="contained"
                    sx={{
                      bgcolor: "#000000",
                      color: "#ffffff",
                      borderRadius: 0,
                      px: 4,
                      textTransform: "none",
                      "&:hover": {
                        bgcolor: "#333333",
                      },
                    }}
                  >
                    {submitting
                      ? "Processing..."
                      : purchaseMethod === "wallet"
                        ? "Invest with Wallet"
                        : paymentMethod === "card"
                          ? "Pay & Invest"
                          : "Generate Transfer Account"}
                  </Button>
                )
              )}
            </>
          )}
      </DialogActions>
    </Dialog>
  );
}

export default TokenDetailsModal;
